import { Headset, Languages, Search, Star } from 'lucide-react';
import { SECTION } from './siteHeaderModel';
import { WEBSITE_DEFAULTS } from './headerDefaults';
import { SUPPORT_ICON_OPTIONS } from './headerIcons';
import { BilingualInput, LinkField, SectionCard, Segmented, SwitchRow, useFieldError } from './editorControls';
import styles from './HeaderEditor.module.css';

// The smaller blocks of the bar: search, customer service, reviews and the language switch.

export function SearchSection({ value, onPatch }) {
  return (
    <SectionCard
      id={SECTION.search}
      icon={Search}
      tone="blue"
      title="Search"
      description="The destination and hotel search in the middle of the bar. On phones it sits in the menu."
      enabled={value.enabled}
      onToggle={(enabled) => onPatch({ enabled })}
      showOn={value.showOn}
      onShowOn={(showOn) => onPatch({ showOn })}
    >
      <BilingualInput
        label="Placeholder"
        value={value.placeholder}
        defaults={WEBSITE_DEFAULTS.searchPlaceholder}
        onChange={(placeholder) => onPatch({ placeholder })}
      />
      <SwitchRow
        title="Suggestions"
        description="Popular destinations as quick picks when the search box is opened."
        checked={value.showSuggestions}
        onChange={(showSuggestions) => onPatch({ showSuggestions })}
      />
    </SectionCard>
  );
}

export function SupportSection({ value, onPatch, errorCount }) {
  return (
    <SectionCard
      id={SECTION.support}
      icon={Headset}
      tone="orange"
      title="Customer service"
      description="The way through to help, beside the search. On phones it stays in the bar next to the menu button."
      enabled={value.enabled}
      onToggle={(enabled) => onPatch({ enabled })}
      showOn={value.showOn}
      onShowOn={(showOn) => onPatch({ showOn })}
      errorCount={errorCount}
    >
      <div className={styles.grid2}>
        <BilingualInput
          label="Label"
          value={value.label}
          defaults={WEBSITE_DEFAULTS.customerService}
          onChange={(label) => onPatch({ label })}
        />
        <LinkField
          value={value.link}
          placeholder="/faq"
          errorPath="support.link"
          hint="Empty opens the help centre (/faq)."
          newTab={value.newTab}
          onChange={(link) => onPatch({ link })}
          onNewTab={(newTab) => onPatch({ newTab })}
        />
      </div>
      <div className={styles.grid2}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Icon</label>
          <Segmented
            value={value.icon}
            ariaLabel="Customer service icon"
            options={SUPPORT_ICON_OPTIONS}
            onChange={(icon) => onPatch({ icon })}
          />
        </div>
        <SwitchRow
          title="Show the label"
          description="Off shows the icon alone. The label also steps aside on narrower screens."
          checked={value.showLabel}
          onChange={(showLabel) => onPatch({ showLabel })}
        />
      </div>
    </SectionCard>
  );
}

export function ReviewsSection({ value, onPatch }) {
  const scoreError = useFieldError('trustpilot.score');
  return (
    <SectionCard
      id={SECTION.reviews}
      icon={Star}
      tone="green"
      title="Reviews (Trustpilot)"
      description="Your Trustpilot rating in the homepage's top strip, and a review link in the bar."
      enabled={value.enabled}
      onToggle={(enabled) => onPatch({ enabled })}
    >
      <div className={styles.grid2}>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="hdr-trust-score">TrustScore</label>
          <input
            id="hdr-trust-score"
            className={`${styles.input} ${scoreError ? styles.inputInvalid : ''}`}
            aria-invalid={scoreError ? 'true' : undefined}
            inputMode="decimal"
            placeholder="e.g. 4,9"
            value={value.score ?? ''}
            onChange={(e) => onPatch({ score: e.target.value.trim() === '' ? null : e.target.value })}
          />
          {scoreError
            ? <span className={styles.fieldError} role="alert">{scoreError}</span>
            : <span className={styles.hint}>Copy it from your Trustpilot page (out of 5). Empty shows a plain "read our reviews" link instead.</span>}
        </div>
      </div>
      <p className={styles.note}>
        On the homepage the top strip shows the stars, Trustpilot's word for the score ("Uitstekend")
        and the score, linking to your review profile. Keep it in step with your Trustpilot page: it
        is shown exactly as typed here and is never estimated. On other pages the bar shows the
        "Trustpilot / Lees onze reviews" link (a small star below 1700px wide, hidden below 1180px).
        It never goes in the phone menu, so there is no device choice.
      </p>
    </SectionCard>
  );
}

export function LanguageSection({ value, onPatch }) {
  return (
    <SectionCard
      id={SECTION.language}
      icon={Languages}
      tone="blue"
      title="Language switch"
      description="The Dutch / English selector, just before the account entry."
      enabled={value.enabled}
      onToggle={(enabled) => onPatch({ enabled })}
      showOn={value.showOn}
      onShowOn={(showOn) => onPatch({ showOn })}
    >
      <p className={styles.note}>
        Switched off, the header offers no way to change the language.
      </p>
    </SectionCard>
  );
}
